import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// SYSTEM PROMPTS MANAGER
// ============================================================

export const SystemPromptsManager = () => {
  const theme = useThemeClasses();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [testingPrompt, setTestingPrompt] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'lithophane',
    prompt_text: '',
    variables: [],
    is_active: true,
    is_default: false,
    tags: []
  });

  useEffect(() => {
    loadPrompts();
  }, [filterCategory]);

  const loadPrompts = async () => {
    try {
      const url = filterCategory 
        ? `${API}/admin/ai-products/prompts?category=${filterCategory}`
        : `${API}/admin/ai-products/prompts`;
      const response = await axios.get(url);
      setPrompts(response.data);
    } catch (error) {
      console.error('Failed to load prompts:', error);
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPrompt) {
        await axios.put(`${API}/admin/ai-products/prompts/${editingPrompt.id}`, formData);
        toast.success('Prompt updated successfully');
      } else {
        await axios.post(`${API}/admin/ai-products/prompts`, formData);
        toast.success('Prompt created successfully');
      }
      setShowForm(false);
      setEditingPrompt(null);
      resetForm();
      loadPrompts();
    } catch (error) {
      console.error('Failed to save prompt:', error);
      toast.error('Failed to save prompt');
    }
  };

  const handleTest = async (promptId) => {
    try {
      setTestingPrompt(promptId);
      const response = await axios.post(`${API}/admin/ai-products/prompts/${promptId}/test`, {
        variables: { theme: 'christmas', size: '75mm' }
      });
      setTestResult(response.data);
      toast.success('Test completed successfully');
    } catch (error) {
      console.error('Test failed:', error);
      toast.error('Test failed');
      setTestResult({ success: false, error: error.message });
    } finally {
      setTestingPrompt(null);
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    try {
      await axios.delete(`${API}/admin/ai-products/prompts/${promptId}`);
      toast.success('Prompt deleted successfully');
      loadPrompts();
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      toast.error('Failed to delete prompt');
    }
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      category: prompt.category,
      prompt_text: prompt.prompt_text,
      variables: prompt.variables || [],
      is_active: prompt.is_active,
      is_default: prompt.is_default,
      tags: prompt.tags || []
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'lithophane',
      prompt_text: '',
      variables: [],
      is_active: true,
      is_default: false,
      tags: []
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading prompts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold">System Prompts Library</h3>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={`px-4 py-2 border rounded-lg ${theme.input}`}
          >
            <option value="">All Categories</option>
            <option value="lithophane">Lithophane</option>
            <option value="general_3d">General 3D</option>
            <option value="figurine">Figurine</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <div className="flex space-x-3">
          {prompts.length === 0 && !loading && (
            <button
              onClick={async () => {
                try {
                  const response = await axios.post(`${API}/admin/ai-products/prompts/seed-defaults`);
                  if (response.data.success) {
                    toast.success('Default prompts seeded successfully');
                    loadPrompts();
                  }
                } catch (error) {
                  console.error('Failed to seed prompts:', error);
                  toast.error('Failed to seed prompts');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              🌱 Seed Default Prompts
            </button>
          )}
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingPrompt(null);
              resetForm();
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {showForm ? 'Cancel' : '+ New Prompt'}
          </button>
        </div>
      </div>

      {/* Prompt Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className={`${theme.card} rounded-lg shadow p-6 space-y-4`}>
          <h4 className="font-semibold text-lg">
            {editingPrompt ? 'Edit Prompt' : 'New Prompt Template'}
          </h4>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Prompt Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              >
                <option value="lithophane">Lithophane</option>
                <option value="general_3d">General 3D</option>
                <option value="figurine">Figurine</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="2"
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Prompt Text *</label>
            <textarea
              value={formData.prompt_text}
              onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
              required
              rows="8"
              placeholder="You are an AI assistant for 3D lithophane generation. Use {theme} for theme and {size} for size..."
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
            <p className="text-xs text-gray-500 mt-1">Use {'{variable}'} syntax for placeholders</p>
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="text-purple-600"
              />
              <span className="text-sm">Active</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="text-purple-600"
              />
              <span className="text-sm">Set as Default for Category</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingPrompt(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {editingPrompt ? 'Update Prompt' : 'Create Prompt'}
            </button>
          </div>
        </form>
      )}

      {/* Prompts List */}
      <div className="grid gap-4">
        {prompts.map((prompt) => (
          <div key={prompt.id} className={`${theme.card} rounded-lg shadow p-6`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-lg font-semibold">{prompt.name}</h4>
                  <span className={`text-xs px-2 py-1 rounded ${
                    prompt.is_active 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {prompt.is_active ? '● Active' : '○ Inactive'}
                  </span>
                  {prompt.is_default && (
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                      ⭐ Default
                    </span>
                  )}
                  <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                    {prompt.category}
                  </span>
                </div>
                {prompt.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{prompt.description}</p>
                )}
                <div className={`p-3 rounded-lg ${theme.bgSecondary} mb-3`}>
                  <p className="text-sm font-mono whitespace-pre-wrap">{prompt.prompt_text}</p>
                </div>
                <div className="flex space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>Used: {prompt.usage_count || 0} times</span>
                  {prompt.success_rate > 0 && <span>Success Rate: {prompt.success_rate}%</span>}
                </div>
              </div>
              <div className="flex flex-col space-y-2 ml-4">
                <button
                  onClick={() => handleTest(prompt.id)}
                  disabled={testingPrompt === prompt.id}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {testingPrompt === prompt.id ? 'Testing...' : 'Test'}
                </button>
                <button
                  onClick={() => handleEdit(prompt)}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(prompt.id)}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Test Result Modal */}
      {testResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card} rounded-lg shadow-xl p-6 max-w-2xl w-full m-4`}>
            <h4 className="font-semibold text-lg mb-4">
              {testResult.success ? '✅ Test Successful' : '❌ Test Failed'}
            </h4>
            {testResult.success ? (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${theme.bgSecondary}`}>
                  <h5 className="font-semibold mb-2">Response:</h5>
                  <p className="text-sm whitespace-pre-wrap">{testResult.response}</p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>Model: <strong>{testResult.model_used}</strong></div>
                  <div>Time: <strong>{testResult.response_time}s</strong></div>
                  <div>Cost: <strong>${testResult.estimated_cost}</strong></div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <p className="text-red-700 dark:text-red-300">{testResult.error}</p>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setTestResult(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BACKGROUND THEMES MANAGER
// ============================================================

export const BackgroundThemesManager = () => {
  const theme = useThemeClasses();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [formData, setFormData] = useState({
    theme_name: '',
    category: 'Seasons',
    icon: '🎨',
    style_tokens: [],
    theme_items: [],
    palette: [],
    mood: 'neutral',
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-products/themes`);
      setThemes(response.data);
    } catch (error) {
      console.error('Failed to load themes:', error);
      toast.error('Failed to load themes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTheme) {
        await axios.put(`${API}/admin/ai-products/themes/${editingTheme.id}`, formData);
        toast.success('Theme updated successfully');
      } else {
        await axios.post(`${API}/admin/ai-products/themes`, formData);
        toast.success('Theme created successfully');
      }
      setShowForm(false);
      setEditingTheme(null);
      resetForm();
      loadThemes();
    } catch (error) {
      console.error('Failed to save theme:', error);
      toast.error('Failed to save theme');
    }
  };

  const handleDelete = async (themeId, isSystemTheme) => {
    if (isSystemTheme) {
      toast.error('System themes cannot be deleted. You can edit them instead.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this theme?')) return;
    try {
      await axios.delete(`${API}/admin/ai-products/themes/${themeId}`);
      toast.success('Theme deleted successfully');
      loadThemes();
    } catch (error) {
      console.error('Failed to delete theme:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete theme';
      toast.error(errorMsg);
    }
  };

  const handleEdit = (thm) => {
    setEditingTheme(thm);
    setFormData({
      theme_name: thm.theme_name,
      category: thm.category,
      icon: thm.icon,
      style_tokens: thm.style_tokens || [],
      theme_items: thm.theme_items || [],
      palette: thm.palette || [],
      mood: thm.mood,
      custom_prompt: thm.custom_prompt || '',
      use_custom_prompt: thm.use_custom_prompt || false,
      is_active: thm.is_active,
      sort_order: thm.sort_order
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      theme_name: '',
      category: 'Seasons',
      icon: '🎨',
      style_tokens: [],
      theme_items: [],
      palette: [],
      mood: 'neutral',
      custom_prompt: '',
      use_custom_prompt: false,
      is_active: true,
      sort_order: 0
    });
  };

  const addArrayItem = (field, value) => {
    if (value.trim()) {
      setFormData({
        ...formData,
        [field]: [...formData[field], value.trim()]
      });
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading themes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">AI Background Themes</h3>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingTheme(null);
            resetForm();
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          {showForm ? 'Cancel' : '+ New Theme'}
        </button>
      </div>

      {/* Theme Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className={`${theme.card} rounded-lg shadow p-6 space-y-4`}>
          <h4 className="font-semibold text-lg">
            {editingTheme ? 'Edit Theme' : 'New Background Theme'}
          </h4>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Theme Name *</label>
              <input
                type="text"
                value={formData.theme_name}
                onChange={(e) => setFormData({ ...formData, theme_name: e.target.value })}
                required
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              >
                <option value="Seasons">Seasons</option>
                <option value="Holidays">Holidays</option>
                <option value="Special Occasions">Special Occasions</option>
                <option value="Memorials">Memorials</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Icon</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Mood</label>
              <select
                value={formData.mood}
                onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              >
                <option value="neutral">Neutral</option>
                <option value="festive">Festive</option>
                <option value="minimalist">Minimalist</option>
                <option value="romantic">Romantic</option>
                <option value="rustic">Rustic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Sort Order</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
          </div>

          {/* Custom Prompt Section */}
          <div className="border-t pt-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <h5 className="font-semibold text-sm mb-2">✨ Custom AI Prompt (Optional)</h5>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Write your own prompt for the AI to generate backgrounds for this theme. 
                When enabled, this will override the auto-generated prompt based on theme attributes.
              </p>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  checked={formData.use_custom_prompt}
                  onChange={(e) => setFormData({ ...formData, use_custom_prompt: e.target.checked })}
                  className="text-purple-600"
                />
                <label className="text-sm font-medium">Use Custom Prompt</label>
              </div>
            </div>

            {formData.use_custom_prompt && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Custom Prompt Text
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.custom_prompt}
                  onChange={(e) => setFormData({ ...formData, custom_prompt: e.target.value })}
                  required={formData.use_custom_prompt}
                  rows="8"
                  placeholder="Example: Create a photorealistic black and white winter scene with a decorated Christmas tree in the center, soft snow falling, cozy warm lighting, festive ornaments, and a wide panoramic view suitable for displaying photo ornaments. The image must be pure greyscale with rich tonal range..."
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Include details about black/white requirements, mood, elements, and composition for best results.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm">Active (visible to customers)</label>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingTheme(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {editingTheme ? 'Update Theme' : 'Create Theme'}
            </button>
          </div>
        </form>
      )}

      {/* Themes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {themes.map((thm) => (
          <div key={thm.id} className={`${theme.card} rounded-lg shadow p-4`}>
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-3xl">{thm.icon}</span>
                <div>
                  <h4 className="font-semibold">{thm.theme_name}</h4>
                  <span className="text-xs text-gray-500">{thm.category}</span>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${
                thm.is_active 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {thm.is_active ? '●' : '○'}
              </span>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              <div>Mood: <strong>{thm.mood}</strong></div>
              <div>Order: <strong>{thm.sort_order}</strong></div>
              <div className="flex flex-wrap gap-2 mt-2">
                {thm.use_custom_prompt && (
                  <span className="px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs">
                    ✨ Custom Prompt
                  </span>
                )}
                {thm.is_system_theme && (
                  <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs">
                    🔒 System Theme
                  </span>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(thm)}
                className="flex-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(thm.id, thm.is_system_theme)}
                disabled={thm.is_system_theme}
                className={`px-3 py-1 text-sm rounded ${
                  thm.is_system_theme 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
                title={thm.is_system_theme ? 'System themes cannot be deleted' : 'Delete theme'}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
